import { supabase } from '../../../lib/supabase'
import { CSV_HEADER_MAP } from '../types'

const BATCH_SIZE = 500

function parseCSVLine(line: string): string[] {
  const result: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]
    if (char === '"') {
      inQuotes = !inQuotes
    } else if (char === ',' && !inQuotes) {
      result.push(current)
      current = ''
    } else {
      current += char
    }
  }
  result.push(current)
  return result
}

export async function checkIfEmpty(): Promise<boolean> {
  const { count, error } = await supabase
    .from('companies')
    .select('*', { count: 'exact', head: true })

  if (error) {
    console.error('Error checking companies count:', error)
    return true
  }
  return (count || 0) === 0
}

interface ImportProgress {
  phase: string
  current: number
  total: number
}

export async function importFromCSV(
  onProgress?: (progress: ImportProgress) => void
): Promise<{ imported: number; duplicatesTagged: number }> {
  // Phase 1: Parse duplicate review CSV to build lookup
  onProgress?.({ phase: 'Loading duplicate analysis...', current: 0, total: 0 })

  const dupLookup = new Map<number, { groupId: string; confidence: string; matchReasons: string }>()

  try {
    const dupResponse = await fetch('/merge-review/DUPLICATE_RECORDS_FOR_REVIEW.csv')
    const dupText = await dupResponse.text()
    const dupLines = dupText.trim().split('\n')

    if (dupLines.length > 1) {
      const dupHeaders = parseCSVLine(dupLines[0])

      for (let i = 1; i < dupLines.length; i++) {
        const values = parseCSVLine(dupLines[i])
        const row: Record<string, string> = {}
        dupHeaders.forEach((h, idx) => {
          row[h.trim()] = (values[idx] || '').trim()
        })

        const lineNum = parseInt(row['line_number'])
        if (lineNum) {
          dupLookup.set(lineNum, {
            groupId: row['duplicate_group_id'] || '',
            confidence: row['confidence'] || '',
            matchReasons: row['match_reasons'] || '',
          })
        }
      }
    }
  } catch (err) {
    console.warn('Could not load duplicate analysis CSV, proceeding without duplicate tagging:', err)
  }

  // Phase 2: Parse Companies CSV
  onProgress?.({ phase: 'Loading companies data...', current: 0, total: 0 })

  const response = await fetch('/merge-review/Companies.csv')
  const csvText = await response.text()
  const lines = csvText.trim().split('\n')

  if (lines.length < 2) throw new Error('Companies CSV is empty')

  const headers = parseCSVLine(lines[0])
  const totalRows = lines.length - 1

  // Phase 3: Build records and batch insert
  const { data: userData } = await supabase.auth.getUser()
  const userId = userData?.user?.id || null

  let imported = 0
  let duplicatesTagged = 0
  let batch: Record<string, any>[] = []

  for (let i = 1; i < lines.length; i++) {
    const values = parseCSVLine(lines[i])
    const csvRow: Record<string, string> = {}
    headers.forEach((h, idx) => {
      csvRow[h.trim()] = (values[idx] || '').trim()
    })

    // Map CSV headers to DB column names
    const record: Record<string, any> = {
      original_line_number: i + 1, // line 2 = first data row
      created_by: userId,
      updated_by: userId,
    }

    for (const [csvHeader, dbCol] of Object.entries(CSV_HEADER_MAP)) {
      record[dbCol] = csvRow[csvHeader] || ''
    }

    // Tag duplicates from the lookup
    const lineNumber = i + 1
    const dupInfo = dupLookup.get(lineNumber)
    if (dupInfo) {
      record.duplicate_group_id = dupInfo.groupId
      record.duplicate_confidence = dupInfo.confidence
      record.duplicate_match_reasons = dupInfo.matchReasons
      duplicatesTagged++
    }

    batch.push(record)

    if (batch.length >= BATCH_SIZE) {
      onProgress?.({ phase: 'Importing companies...', current: imported, total: totalRows })
      const { error } = await supabase.from('companies').insert(batch)
      if (error) {
        console.error(`Batch insert failed at row ${imported}:`, error)
        throw new Error(`Import failed at row ${imported}: ${error.message}`)
      }
      imported += batch.length
      batch = []
    }
  }

  // Insert remaining rows
  if (batch.length > 0) {
    onProgress?.({ phase: 'Importing companies...', current: imported, total: totalRows })
    const { error } = await supabase.from('companies').insert(batch)
    if (error) {
      console.error('Final batch insert failed:', error)
      throw new Error(`Import failed: ${error.message}`)
    }
    imported += batch.length
  }

  onProgress?.({ phase: 'Done!', current: imported, total: totalRows })
  return { imported, duplicatesTagged }
}
